import { useEffect, useState, useCallback, ReactNode } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';

export interface MapMarker {
  id: string;
  coordinates: {
    lat: number;
    long: number;
  };
  data?: unknown;
  popupContent?: ReactNode;
  iconUrl?: string;
  iconSize?: [number, number];
  iconAnchor?: [number, number];
  popupAnchor?: [number, number];
}

export interface GenericMapProps {
  markers: MapMarker[];
  center?: [number, number];
  zoom?: number;
  height?: string;
  className?: string;
  loading?: boolean;
  onMarkerClick?: (marker: MapMarker) => void;
  renderPopup?: (marker: MapMarker) => ReactNode;
  defaultIconUrl?: string;
  defaultIconSize?: [number, number];
  defaultIconAnchor?: [number, number];
  defaultPopupAnchor?: [number, number];
}

const GenericMap = ({
  markers,
  center = [14.1, -87.2], // Default to Honduras center
  zoom = 6,
  height = '400px',
  className = '',
  loading = false,
  onMarkerClick,
  renderPopup,
  defaultIconUrl = 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
  defaultIconSize = [25, 25],
  defaultIconAnchor = [12, 12],
  defaultPopupAnchor = [0, -12],
}: GenericMapProps) => {
  const [mapKey, setMapKey] = useState(0);
  const { t } = useTranslation('common');

  useEffect(() => {
    setMapKey((prev) => prev + 1);
  }, [markers]);

  const worldBounds: L.LatLngBoundsExpression = [
    [-85, -180],
    [85, 180],
  ];

  const createIcon = useCallback(
    (marker: MapMarker) => {
      return new Icon({
        iconUrl: marker.iconUrl || defaultIconUrl,
        iconSize: marker.iconSize || defaultIconSize,
        iconAnchor: marker.iconAnchor || defaultIconAnchor,
        popupAnchor: marker.popupAnchor || defaultPopupAnchor,
      });
    },
    [defaultIconUrl, defaultIconSize, defaultIconAnchor, defaultPopupAnchor]
  );

  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      if (onMarkerClick) {
        onMarkerClick(marker);
      }
    },
    [onMarkerClick]
  );

  const getPopupContent = useCallback(
    (marker: MapMarker) => {
      if (renderPopup) {
        return renderPopup(marker);
      }
      if (marker?.popupContent) {
        return marker?.popupContent;
      }
      return (
        <div>
          <h3>{t('Marker')} {marker.id}</h3>
        </div>
      );
    },
    [renderPopup]
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">{t('loadingMap')}</p>
        </div>
      </div>
    );
  }

  if (!markers || markers.length === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-center text-gray-500">
          <p>{t('noMarkers')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`} style={{ height }}>
      <MapContainer
        key={mapKey}
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
        minZoom={2}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
        maxBounds={worldBounds}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          noWrap={true}
        />
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={[marker.coordinates.lat, marker.coordinates.long]}
            icon={createIcon(marker)}
            eventHandlers={{
              click: () => handleMarkerClick(marker),
            }}
          >
            <Popup>
              {getPopupContent(marker)}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default GenericMap;
